/* Veaudry PLP filter drawer
 * Desktop: everything sits open inside the drawer; a change commits straight
 *          away, and the "Filter" tab collapses the box.
 * Mobile:  the same form is a bottom sheet, staged until "Apply".
 *
 * Either way the submit belongs to the theme's facets.js — it listens for
 * `input` on #FacetFiltersForm. We intercept during capture so we decide when
 * that fires, and dispatch a synthetic event when we want it to.
 */
if (!customElements.get("veaudry-plp-toolbar")) {
  customElements.define(
    "veaudry-plp-toolbar",
    class VeaudryPlpToolbar extends HTMLElement {
      static MOBILE_BREAKPOINT = 900;
      static DRAWER_KEY = "veaudry:plp-drawer-closed";

      connectedCallback() {
        this.form = this.querySelector("#FacetFiltersForm");
        this.facetWrapper = this.querySelector("facet-filters-form");
        this.scrim = this.querySelector(".v-scrim");
        this.price = this.querySelector(".v-price");
        this.sheetTrigger = this.querySelector("[data-plp-sheet]");
        this.drawerTab = this.querySelector("[data-plp-drawer]");

        this.isApplying = false;
        this.lastMobile = this.isMobile();

        this.onKeydown = this.handleKeydown.bind(this);
        this.onResize = this.handleResize.bind(this);

        this.bind();
        this.restoreDrawer();
        this.syncPrice();
      }

      disconnectedCallback() {
        document.removeEventListener("keydown", this.onKeydown);
        window.removeEventListener("resize", this.onResize);
        this.unlockScroll();
      }

      /* ---------------- wiring ---------------- */

      bind() {
        this.drawerTab?.addEventListener("click", () =>
          this.toggleDrawer(this.classList.contains("is-drawer-closed")),
        );

        this.sheetTrigger?.addEventListener("click", (event) => {
          event.stopPropagation();
          this.toggleSheet(!this.isSheetOpen());
        });

        this.querySelector("[data-plp-close]")?.addEventListener("click", () =>
          this.toggleSheet(false),
        );
        this.scrim?.addEventListener("click", () => this.toggleSheet(false));
        this.facetWrapper?.addEventListener("click", (event) =>
          event.stopPropagation(),
        );

        this.querySelector("[data-plp-clear]")?.addEventListener("click", () =>
          this.clearAll(),
        );
        this.querySelector("[data-plp-apply]")?.addEventListener("click", () => {
          this.toggleSheet(false);
          this.submit();
        });

        /* Hold facets.js back: it submits on any `input` inside the form, so we
           intercept on the wrapper during capture — updating our own UI there,
           since stopping propagation also hides the event from the form's own
           listener — and let it through only for submit()'s synthetic event. */
        (this.facetWrapper || this.form)?.addEventListener(
          "input",
          (event) => {
            if (this.isApplying) return;
            event.stopPropagation();

            const target = event.target;

            if (target.matches("[data-price-range]")) {
              this.syncPrice(event);
              return; /* price commits on `change`, not mid-drag */
            }

            if (target.matches('.v-opt__input[type="checkbox"]')) {
              this.enforceExclusive(target);
            }

            if (!this.isSheetOpen()) this.submit();
          },
          true,
        );

        this.form?.addEventListener("change", (event) => {
          if (event.target.matches("[data-price-range]") && !this.isSheetOpen()) {
            this.submit();
          }
        });

        this.form?.addEventListener("submit", (event) => {
          event.preventDefault();
          this.submit();
        });

        document.addEventListener("keydown", this.onKeydown);
        window.addEventListener("resize", this.onResize);
      }

      handleKeydown(event) {
        if (event.key !== "Escape" || !this.isSheetOpen()) return;
        this.toggleSheet(false);
        this.sheetTrigger?.focus();
      }

      handleResize() {
        const mobile = this.isMobile();
        if (mobile === this.lastMobile) return;
        this.lastMobile = mobile;
        this.toggleSheet(false);
        this.syncPrice();
      }

      /* ---------------- drawer ---------------- */

      isMobile() {
        return window.innerWidth < VeaudryPlpToolbar.MOBILE_BREAKPOINT;
      }

      /* The whole toolbar is re-rendered on every filter change, so a collapsed
         drawer would spring back open after each interaction without this. */
      restoreDrawer() {
        let closed = false;
        try {
          closed =
            sessionStorage.getItem(VeaudryPlpToolbar.DRAWER_KEY) === "1";
        } catch (e) {
          closed = false;
        }
        if (closed) this.toggleDrawer(false, true);
      }

      toggleDrawer(open, silent) {
        this.classList.toggle("is-drawer-closed", !open);
        this.drawerTab?.setAttribute("aria-expanded", String(open));

        if (silent) return;
        try {
          sessionStorage.setItem(
            VeaudryPlpToolbar.DRAWER_KEY,
            open ? "0" : "1",
          );
        } catch (e) {
          /* private mode: the drawer just reopens next render */
        }

        /* handle positions are only measurable once the box is laid out */
        if (open) this.syncPrice();
      }

      /* ---------------- sheet ---------------- */

      isSheetOpen() {
        return this.classList.contains("is-sheet-open");
      }

      toggleSheet(open) {
        this.classList.toggle("is-sheet-open", open);
        this.sheetTrigger?.setAttribute("aria-expanded", String(open));

        if (this.scrim) {
          this.scrim.hidden = !open;
          this.scrim.classList.toggle("is-open", open);
        }

        if (open) {
          this.lockScroll();
          this.syncPrice();
        } else {
          this.unlockScroll();
        }
      }

      lockScroll() {
        if (this.isMobile()) document.body.style.overflow = "hidden";
      }

      unlockScroll() {
        document.body.style.overflow = "";
      }

      /* ---------------- availability ---------------- */

      /* "In stock" and "Out of stock" are the two halves of one binary: ticking
         both returns nothing, which is what ticking neither already means. */
      enforceExclusive(input) {
        const group = input.closest(".v-group--exclusive");
        if (!group || !input.checked) return;

        group.querySelectorAll(".v-opt__input").forEach((other) => {
          if (other !== input) other.checked = false;
        });
      }

      /* ---------------- price ---------------- */

      syncPrice(event) {
        if (!this.price) return;

        const minRange = this.price.querySelector('[data-price-range="min"]');
        const maxRange = this.price.querySelector('[data-price-range="max"]');
        if (!minRange || !maxRange) return;

        const max = Number(this.price.dataset.max) || Number(maxRange.max);
        let from = Number(minRange.value);
        let to = Number(maxRange.value);

        if (from > to) {
          if (event && event.target === minRange) from = to;
          else to = from;
        }
        from = Math.max(0, Math.min(from, max));
        to = Math.max(0, Math.min(to, max));

        minRange.value = from;
        maxRange.value = to;

        this.price.querySelector('[data-price-input="min"]').value = from;
        this.price.querySelector('[data-price-input="max"]').value = to;

        const fill = this.price.querySelector("[data-price-fill]");
        if (fill && max > 0) {
          fill.style.left = (from / max) * 100 + "%";
          fill.style.width = ((to - from) / max) * 100 + "%";
        }

        this.placeValue("min", from, max);
        this.placeValue("max", to, max);
      }

      /* Each value rides under its own handle. The thumb is 13px, so its centre
         travels the track inset by half a thumb at each end. */
      placeValue(which, value, max) {
        const out = this.price.querySelector(`[data-price-out="${which}"]`);
        if (!out) return;

        const currency = out.dataset.currency || "";
        out.textContent = `${currency} ${this.formatPrice(value)}`.trim();

        const track = this.price.querySelector(".v-price__slider");
        if (!track || !max) return;

        const width = track.clientWidth;
        if (!width) return;

        const thumb = 13;
        const ratio = value / max;
        const centre = thumb / 2 + ratio * (width - thumb);
        out.style.left = (centre / width) * 100 + "%";
      }

      formatPrice(value) {
        try {
          return Number(value).toLocaleString(
            document.documentElement.lang || "en",
          );
        } catch (e) {
          return String(value);
        }
      }

      priceIsFullRange() {
        if (!this.price) return true;
        const from = Number(
          this.price.querySelector('[data-price-input="min"]').value,
        );
        const to = Number(
          this.price.querySelector('[data-price-input="max"]').value,
        );
        return from <= 0 && to >= Number(this.price.dataset.max);
      }

      /* ---------------- submit ---------------- */

      clearAll() {
        this.querySelectorAll('.v-opt__input[type="checkbox"]').forEach(
          (input) => {
            input.checked = false;
          },
        );

        if (this.price) {
          const max = Number(this.price.dataset.max);
          this.price.querySelector('[data-price-range="min"]').value = 0;
          this.price.querySelector('[data-price-range="max"]').value = max;
          this.syncPrice();
        }

        this.toggleSheet(false);
        this.submit();
      }

      submit() {
        if (!this.form) return;

        /* keep the URL clean: an untouched price range is not a filter */
        if (this.price) {
          const full = this.priceIsFullRange();
          this.price.querySelector('[data-price-input="min"]').disabled = full;
          this.price.querySelector('[data-price-input="max"]').disabled = full;
        }

        this.isApplying = true;
        this.form.dispatchEvent(new Event("input", { bubbles: true }));
        this.isApplying = false;
      }
    },
  );
}
